/**
 * Cookie-Banner der App, und der Start der Messung (messung.ts).
 *
 * Erscheint nur, wenn es noch keine Wahl gibt, auch nicht auf der Website:
 * beide lesen und schreiben dasselbe Cookie für .99takes.com. Drei gleich
 * erreichbare Wege wie auf der Website: Nur notwendige, Auswahl speichern,
 * Alle akzeptieren. Keiner ist vorausgewählt.
 *
 * Wieder öffnen: `oeffneCookieEinstellungen()` aus messung.ts, im Fuß von AppRahmen.
 */

import { useEffect, useId, useState } from 'react';
import { useLocation } from 'react-router';
import { COOKIE_EINSTELLUNGEN, leseEinwilligung, ohneMessung, speichereEinwilligung, starteMessung } from './messung';
import { t } from './sprache';

export default function Einwilligung() {
  const { pathname } = useLocation();
  const [offen, setOffen] = useState(false);
  const [analytics, setAnalytics] = useState(false);
  const [marketing, setMarketing] = useState(false);
  const basis = useId();

  useEffect(() => {
    if (ohneMessung(pathname)) return;
    starteMessung(pathname);
    if (!leseEinwilligung()) setOffen(true);
  }, [pathname]);

  useEffect(() => {
    const f = () => {
      const e = leseEinwilligung();
      setAnalytics(!!e?.analytics);
      setMarketing(!!e?.marketing);
      setOffen(true);
    };
    window.addEventListener(COOKIE_EINSTELLUNGEN, f);
    return () => window.removeEventListener(COOKIE_EINSTELLUNGEN, f);
  }, []);

  if (!offen || ohneMessung(pathname)) return null;

  const speichern = (a: boolean, m: boolean) => {
    speichereEinwilligung({ analytics: a, marketing: m });
    setOffen(false);
  };

  const knopf = 'inline-flex min-h-[2.5rem] cursor-pointer items-center justify-center rounded-full px-4 text-[0.875rem] font-semibold transition-colors duration-150';

  return (
    <div
      role="dialog" aria-modal="false" aria-labelledby={`${basis}-titel`}
      className="fixed inset-x-3 bottom-3 z-50 mx-auto max-w-[34rem] rounded-2xl border border-linie bg-flaeche p-5 shadow-[0_24px_48px_-20px_rgb(20_18_12/0.45)] sm:inset-x-6 sm:bottom-6"
    >
      <h2 id={`${basis}-titel`} className="m-0 text-[1rem] font-semibold text-tinte">{t('Cookie-Einstellungen', 'Cookie settings')}</h2>
      <p className="mt-1.5 mb-4 text-[0.875rem] leading-relaxed text-text-leise">
        {t(
          <>Mit deiner Zustimmung messen wir, ob Registrierung und Einrichtung klappen. Deine Wahl gilt auch auf
        99takes.com. Mehr in der{' '}
        <a href="https://www.99takes.com/datenschutz" className="text-marke-tief underline underline-offset-[3px]">Datenschutzerklärung</a>.</>,
          <>With your consent we measure whether signing up and setup work. Your choice also applies on
        99takes.com. More in the{' '}
        <a href="https://www.99takes.com/privacy" className="text-marke-tief underline underline-offset-[3px]">privacy policy</a>.</>,
        )}
      </p>
      <div className="mb-4 space-y-2.5">
        <Schalter id={`${basis}-notwendig`} titel={t('Notwendig', 'Necessary')} text={t('Anmeldung und Sitzung', 'Sign-in and session')} an fest />
        <Schalter id={`${basis}-analyse`} titel={t('Analyse', 'Analytics')} text={t('Welche Schritte der Einrichtung klappen', 'Which setup steps work')} an={analytics} onChange={setAnalytics} />
        <Schalter id={`${basis}-marketing`} titel="Marketing" text={t('Ob eine Anzeige zu einer Registrierung geführt hat', 'Whether an ad led to a sign-up')} an={marketing} onChange={setMarketing} />
      </div>
      <div className="grid gap-2 sm:grid-cols-3">
        <button type="button" onClick={() => speichern(false, false)} className={`${knopf} border border-linie bg-flaeche text-text hover:border-linie-stark`}>
          {t('Nur notwendige', 'Necessary only')}
        </button>
        <button type="button" onClick={() => speichern(analytics, marketing)} className={`${knopf} border border-linie bg-flaeche text-text hover:border-linie-stark`}>
          {t('Auswahl speichern', 'Save selection')}
        </button>
        <button type="button" onClick={() => speichern(true, true)} className={`${knopf} border-0 bg-knopf text-auf-marke hover:bg-knopf-hover`}>
          {t('Alle akzeptieren', 'Accept all')}
        </button>
      </div>
    </div>
  );
}

function Schalter({ id, titel, text, an, fest = false, onChange }: {
  id: string; titel: string; text: string; an: boolean; fest?: boolean; onChange?: (v: boolean) => void;
}) {
  return (
    <label htmlFor={id} className={`flex items-center justify-between gap-4 ${fest ? '' : 'cursor-pointer'}`}>
      <span className="min-w-0">
        <span className="block text-[0.875rem] font-medium text-tinte">{titel}</span>
        <span className="block text-[0.8125rem] text-text-hell">{text}</span>
      </span>
      <input
        id={id} type="checkbox" checked={an} disabled={fest}
        onChange={e => onChange?.(e.target.checked)}
        className="size-4 shrink-0 accent-[var(--color-knopf)]"
      />
    </label>
  );
}
